import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, PaperPlaneTilt, Handshake } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNERSHIP_TYPES = [
  "Supplier",
  "Distributor",
  "Manufacturer",
  "Investor",
  "Buyer",
  "Strategic Partner",
  "Other",
];

const INDUSTRIES = [
  "Manufacturing",
  "Construction Materials",
  "Food & Agriculture",
  "Industrial Equipment",
  "Technology",
  "Energy",
  "Healthcare",
  "Logistics",
  "Consumer Goods",
  "Other",
];

const initial = {
  company_name: "",
  contact_person: "",
  position: "",
  country: "",
  industry: "",
  company_website: "",
  email: "",
  phone: "",
  partnership_type: "",
  description: "",
};

export default function PartnershipForm({
  open,
  onClose,
  initialIndustry = "",
  initialPartnershipType = "",
}) {
  const [form, setForm] = useState({
    ...initial,
    industry: initialIndustry,
    partnership_type: initialPartnershipType,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const industryOptions = INDUSTRIES.includes(initialIndustry) || !initialIndustry
    ? INDUSTRIES
    : [initialIndustry, ...INDUSTRIES];

  const typeOptions = PARTNERSHIP_TYPES.includes(initialPartnershipType) || !initialPartnershipType
    ? PARTNERSHIP_TYPES
    : [initialPartnershipType, ...PARTNERSHIP_TYPES];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setForm((p) => ({
        ...p,
        industry: initialIndustry || p.industry,
        partnership_type: initialPartnershipType || p.partnership_type,
      }));
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, initialIndustry, initialPartnershipType]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const close = () => {
    onClose();
    // reset after the exit animation
    setTimeout(() => {
      setForm({
        ...initial,
        industry: initialIndustry,
        partnership_type: initialPartnershipType,
      });
      setSubmitted(false);
      setError("");
    }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.company_name || !form.contact_person || !emailRegex.test(form.email)) {
      setError("Please complete company name, contact person and a valid email.");
      return;
    }
    if (form.description.trim().length < 10) {
      setError("Please provide a brief description (at least 10 characters).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partnership`, form);
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      setError(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 md:p-6 overflow-y-auto"
          data-testid="partnership-modal"
        >
          <div
            className="absolute inset-0 bg-altin-ink/80 backdrop-blur-sm"
            onClick={close}
          />

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl bg-altin-bg my-8 shadow-2xl border border-altin-border"
          >
            {/* header */}
            <div className="flex items-start justify-between gap-6 p-8 md:p-10 border-b border-altin-border bg-white">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="gold-divider" />
                  <span className="overline">Become a Partner</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-altin-ink tracking-tight">
                  Partnership Application
                </h2>
              </div>
              <button
                onClick={close}
                data-testid="partnership-close"
                aria-label="Close"
                className="w-10 h-10 grid place-items-center border border-altin-border text-altin-ink hover:border-altin-gold hover:text-altin-gold transition-colors shrink-0"
              >
                <X size={20} weight="light" />
              </button>
            </div>

            <div className="p-8 md:p-10">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="py-10 text-center"
                    data-testid="partnership-success"
                  >
                    <div className="w-20 h-20 rounded-full grid place-items-center border border-altin-gold text-altin-gold mx-auto mb-8">
                      <CheckCircle size={40} weight="light" />
                    </div>
                    <h3 className="font-serif-display text-3xl text-altin-ink tracking-tight">
                      Thank you for your application.
                    </h3>
                    <p className="mt-4 text-altin-muted leading-relaxed max-w-lg mx-auto">
                      Your partnership proposal has been received by the Altin
                      Link team. Our business development office will review your
                      submission and respond to qualified enquiries within two
                      business days. All information is treated with strict
                      confidentiality.
                    </p>
                    <button
                      onClick={close}
                      data-testid="partnership-success-close"
                      className="mt-8 inline-flex items-center gap-3 bg-altin-ink text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold transition-colors"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    data-testid="partnership-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-8"
                  >
                    <p className="text-sm text-altin-muted leading-relaxed">
                      Tell us about your company and the partnership you envision.
                      Fields marked * are required.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
                      <Field id="company_name" label="Company Name *" value={form.company_name} onChange={update("company_name")} />
                      <Field id="contact_person" label="Contact Person *" value={form.contact_person} onChange={update("contact_person")} />
                      <Field id="position" label="Position" value={form.position} onChange={update("position")} />
                      <Field id="country" label="Country" value={form.country} onChange={update("country")} />
                      <SelectField id="industry" label="Industry" value={form.industry} onChange={update("industry")} options={industryOptions} placeholder="Select industry" />
                      <Field id="company_website" label="Company Website" value={form.company_website} onChange={update("company_website")} placeholder="https://" />
                      <Field id="email" label="Email Address *" type="email" value={form.email} onChange={update("email")} />
                      <Field id="phone" label="Phone Number" value={form.phone} onChange={update("phone")} />
                      <div className="sm:col-span-2">
                        <SelectField id="partnership_type" label="Type of Partnership" value={form.partnership_type} onChange={update("partnership_type")} options={typeOptions} placeholder="Select partnership type" />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="description" className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2">
                          Brief Description of the Proposal *
                        </label>
                        <textarea
                          id="description"
                          data-testid="partnership-input-description"
                          rows={4}
                          value={form.description}
                          onChange={update("description")}
                          placeholder="Describe your company, offering and what you are looking for..."
                          className="w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 text-altin-ink placeholder:text-altin-muted/60 transition-colors resize-none"
                        />
                      </div>
                    </div>

                    {error && (
                      <p data-testid="partnership-error" className="text-sm text-destructive">
                        {error}
                      </p>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-2">
                      <p className="text-xs text-altin-muted max-w-xs flex items-center gap-2">
                        <Handshake size={16} weight="light" className="text-altin-gold shrink-0" />
                        Submissions are stored securely and handled confidentially.
                      </p>
                      <button
                        type="submit"
                        data-testid="partnership-submit"
                        disabled={loading}
                        className="group inline-flex items-center gap-3 bg-altin-ink text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loading ? "Submitting..." : "Submit Application"}
                        <PaperPlaneTilt weight="light" className="transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function Field({ id, label, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2">
        {label}
      </label>
      <input
        id={id}
        data-testid={`partnership-input-${id}`}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 text-altin-ink placeholder:text-altin-muted/60 transition-colors"
      />
    </div>
  );
}

function SelectField({ id, label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[10px] uppercase tracking-[0.25em] text-altin-muted mb-2">
        {label}
      </label>
      <select
        id={id}
        data-testid={`partnership-input-${id}`}
        value={value}
        onChange={onChange}
        className={`w-full bg-transparent border-b border-altin-border focus:border-altin-gold focus:outline-none py-3 transition-colors cursor-pointer ${
          value ? "text-altin-ink" : "text-altin-muted/60"
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o} className="text-altin-ink">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
